import { t } from '../../i18next';
import {
  compareAddedRowObjects,
  compareEditedRowObjects,
} from '../../shared/utils/commitHandlerHelper';
import { postRequest } from '../../shared/utils';
import { processDeletedRows } from './services/processDeletedRows';
import { processMeDoneNotifications } from './services/processMeDoneNotifications';
import { processMeStoppedNotifications } from './services/processMeStoppedNotifications';
import { processDownstreamStatusUpdateToAvNotifications } from './services/processDownstreamStatusUpdateToAvNotifications';
import { resolveRowDeleteNotificationsForDeletedRows } from './services/resolveRowDeleteNotificationsForDeletedRows';
import { resolvePostSaveRoutingMessage } from './services/resolvePostSaveRoutingMessage.ts';
import { processAvToDownstreamHandoffNotifications } from './services/processAvToDownstreamHandoffNotifications';
import { resolveSessionRoleAccessMap } from './services/sessionRoleAccess';
import { getDeleteErrorMessage } from './services/getDeleteErrorMessage';

const ROLE_NAMES = ['ME', 'AV', 'Administrator', 'External Collaborator'];

export const CommitHandler = async (props) => {
  const {
    allAccessibleColumns,
    tableDetails,
    editedRow,
    addedRow,
    deletedRow,
    editedRowOriginal,
    userDetails,
    configSchema,
  } = props;
  const schemaName = tableDetails.mbdDataGrid.schemaName;
  const resultSet = {
    success: true,
    errors: [],
    warnings: [],
  };
  const effectiveConfigSchema = resolveConfigSchema(configSchema);

  const mutationTables = getMutationTableList({ allAccessibleColumns });

  const data = compareAddedRowObjects(addedRow);
  const newRow = rowHandler(mutationTables, data);
  const [, muttationId] = mutationTables.sheet1[0].mutationTablePkey.split(':');
  const insertObjects = newRow.flatMap((row) => {
    const newData = removeSheetId1(row, muttationId);
    newData.forEach((item) => (item.created_by = userDetails.userId));
    return newData;
  });

  const updates = rowHandler(mutationTables, editedRow);
  const sheet1UpdateObjects = updates.map((item) => {
    const sheet1Set = compareEditedRowObjects(item.sheet1, editedRowOriginal, 'id1');
    sheet1Set['updated_by'] = userDetails.userId;
    sheet1Set['update_date'] = new Date().toISOString().substring(0, 19);
    const where = {};
    where['id1'] = { _eq: item.sheet1.id1 };
    return {
      where,
      _set: sheet1Set,
    };
  });
  const npiUpdateObjects = updates
    .filter((item) => !!item.npi.npi_id)
    .map((item) => {
      const npiSet = compareEditedRowObjects(item.npi, editedRowOriginal, 'npi_id');
      npiSet['updated_by'] = userDetails.userId;
      npiSet['update_date'] = new Date().toISOString().substring(0, 19);
      const where = {};
      where['npi_id'] = { _eq: item.npi.npi_id };
      return {
        where,
        _set: npiSet,
      };
    });

  const roleAccessMap = await resolveRoleAccessMap({
    editedRow,
    deletedRow,
    configSchema: effectiveConfigSchema,
  });

  if (!roleAccessMap) {
    resultSet.errors.push({
      message: t('npiSteering.insert_upsert_error', {
        htz: 'data',
        msg: 'Unable to validate role access from active session',
      }),
    });
    resultSet.success = false;
    return resultSet;
  }

  const meToAvNotificationError = await processMeDoneNotifications({
    editedRow,
    editedRowOriginal,
    allAccessibleColumns,
    hasMeRole: !!roleAccessMap['ME'],
    screenGridId: Number(tableDetails.id),
    createdBy: Number(userDetails.userId),
    configSchema: effectiveConfigSchema,
  });

  if (meToAvNotificationError) {
    resultSet.errors.push({
      message: t('npiSteering.insert_upsert_error', {
        htz: 'data',
        msg: meToAvNotificationError,
      }),
    });
    resultSet.success = false;
    return resultSet;
  }

  const meStoppedNotificationError = await processMeStoppedNotifications({
    editedRow,
    editedRowOriginal,
    allAccessibleColumns,
    hasMeRole: !!roleAccessMap['ME'],
    screenGridId: Number(tableDetails.id),
    createdBy: Number(userDetails.userId),
    configSchema: effectiveConfigSchema,
  });

  if (meStoppedNotificationError) {
    resultSet.errors.push({
      message: t('npiSteering.insert_upsert_error', {
        htz: 'data',
        msg: meStoppedNotificationError,
      }),
    });
    resultSet.success = false;
    return resultSet;
  }

  const avToDownstreamNotificationError = await processAvToDownstreamHandoffNotifications({
    editedRow,
    editedRowOriginal,
    allAccessibleColumns,
    hasAvRole: !!roleAccessMap['AV'],
    screenGridId: Number(tableDetails.id),
    createdBy: Number(userDetails.userId),
    configSchema: effectiveConfigSchema,
  });

  if (avToDownstreamNotificationError) {
    resultSet.errors.push({
      message: t('npiSteering.insert_upsert_error', {
        htz: 'data',
        msg: avToDownstreamNotificationError,
      }),
    });
    resultSet.success = false;
    return resultSet;
  }

  const downstreamStatusUpdateNotificationError =
    await processDownstreamStatusUpdateToAvNotifications({
      editedRow,
      editedRowOriginal,
      allAccessibleColumns,
      screenGridId: Number(tableDetails.id),
      createdBy: Number(userDetails.userId),
      configSchema: effectiveConfigSchema,
    });

  if (downstreamStatusUpdateNotificationError) {
    resultSet.errors.push({
      message: t('npiSteering.insert_upsert_error', {
        htz: 'data',
        msg: downstreamStatusUpdateNotificationError,
      }),
    });
    resultSet.success = false;
    return resultSet;
  }

  const deleteResult = await processDeletedRows({
    deletedRow,
    roleAccessMap,
    screenGridId: Number(tableDetails.id),
    userId: Number(userDetails.userId),
    sheet1UpdateObjects,
    resolveSheet1DeleteIds: (rows) => {
      const deletes = rowHandler(mutationTables, rows);
      return deletes.map((item) => item.sheet1.id1);
    },
  });

  if (!deleteResult.success) {
    const deleteErrorMessage = getDeleteErrorMessage(deleteResult);

    resultSet.errors.push({
      message: deleteErrorMessage,
    });
    resultSet.success = false;
    return resultSet;
  }

  const sheet1DeleteIds = deleteResult.sheet1DeleteIds;

  const sheet1Payload = {
    deleteIds: sheet1DeleteIds,
    insertObjects,
    pkey: 'id1',
    schema: schemaName,
    table: 'sheet1',
    updateObjects: sheet1UpdateObjects,
  };
  const npiPayload = {
    pkey: 'npi_id',
    schema: schemaName,
    table: 'npi',
    updateObjects: npiUpdateObjects,
  };

  const result = await postRequest('/api/tables', {
    screenGridId: tableDetails.id,
    tables: [npiPayload, sheet1Payload],
  });
  if (result.errors) {
    resultSet.errors.push({
      message: t('npiSteering.insert_upsert_error', {
        htz: 'data',
        msg: result.errors[0]?.message,
      }),
    });
    resultSet.success = false;
    return resultSet;
  }

  const isAdministrator = !!roleAccessMap['Administrator'];
  if (isAdministrator) {
    await resolveRowDeleteNotificationsForDeletedRows({
      screenGridId: Number(tableDetails.id),
      deletedRowIds: sheet1DeleteIds,
    });
  }

  const postSaveRoutingMessage = resolvePostSaveRoutingMessage({
    changedRows: [...addedRow, ...editedRow],
  });

  if (postSaveRoutingMessage) {
    resultSet.warnings.push({
      message: postSaveRoutingMessage,
    });
  }

  return resultSet;
};

const getMutationTableList = (props) => {
  const { allAccessibleColumns } = props;
  const list = allAccessibleColumns.reduce((resp, item) => {
    const { mutationTableName } = item;
    if (!mutationTableName) {
      return resp;
    }
    if (!resp?.[mutationTableName]) {
      resp[mutationTableName] = [];
    }
    resp[mutationTableName].push(item);
    return resp;
  }, {});
  return list;
};

const rowHandler = (mutationTables, rowDetails) => {
  const reqData = rowDetails.reduce((data, item) => {
    const groupRecord = {};
    groupRecord['rowData'] = item;
    for (const key in mutationTables) {
      groupRecord[key] = mutationTables[key].reduce((resp, record) => {
        const { mutationColumnName, attrInternalName } = record;
        resp[mutationColumnName ?? attrInternalName] = item[attrInternalName];
        return resp;
      }, {});
    }
    data.push(groupRecord);
    return data;
  }, []);
  return reqData;
};

const removeSheetId1 = (item, muttationId) => {
  const itemDetails = [{ ...item.sheet1 }];
  for (const col of itemDetails) {
    delete col[muttationId];
  }
  return itemDetails;
};

async function resolveRoleAccessMap({
  editedRow,
  deletedRow,
  configSchema,
}: {
  editedRow: Array<Record<string, unknown>>;
  deletedRow: Array<Record<string, unknown>>;
  configSchema: string;
}): Promise<Record<string, boolean> | null> {
  if (!editedRow.length && !deletedRow.length) {
    return {};
  }

  // Resolve role access map from session token for the given roles
  const roles = resolveSessionRoleAccessMap(ROLE_NAMES, configSchema);
  return roles;
}

function resolveConfigSchema(configSchema: unknown): string {
  return typeof configSchema === 'string' && configSchema ? configSchema : 'mbdcm';
}
